import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Check, Copy, Menu, X } from 'lucide-react';
import { Logo } from '@/components/brand/Logo';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/cn';
import { BAGIAN } from '@/content/dokumentasi';

/**
 * Dokumentasi untuk pengguna, di dalam aplikasi.
 *
 * Sengaja BUKAN salinan dokumen di folder `docs/`. Yang di sana ditujukan
 * untuk orang yang mengembangkan Crawel: arsitektur, model data, keputusan
 * teknis. Yang di sini untuk orang yang memakainya, dan pertanyaannya
 * berbeda: bagaimana memulai, kenapa hasilnya kosong, bagaimana memanggil
 * dari program lain.
 *
 * Halamannya publik. Calon pengguna harus bisa menilai apakah Crawel cocok
 * sebelum membuat akun, dan dokumentasi di balik dinding login membuat itu
 * mustahil.
 */

/** Menyalin ke papan klip, dengan penanda berhasil yang hilang sendiri. */
const TombolSalin = ({ teks }) => {
  const [tersalin, setTersalin] = useState(false);

  useEffect(() => {
    if (!tersalin) return undefined;
    const id = setTimeout(() => setTersalin(false), 1800);
    return () => clearTimeout(id);
  }, [tersalin]);

  return (
    <button
      type="button"
      onClick={() => {
        navigator.clipboard?.writeText(teks).then(() => setTersalin(true));
      }}
      className="absolute top-2 right-2 rounded-md border border-white/15 bg-white/10 p-1.5 text-white/70 transition-colors hover:bg-white/20 hover:text-white"
      aria-label={tersalin ? 'Tersalin' : 'Salin kode'}
    >
      {tersalin ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
    </button>
  );
};

const Kode = ({ children }) => (
  <div className="relative my-3">
    <pre className="overflow-x-auto rounded-lg bg-slate-900 p-4 font-mono text-xs leading-relaxed text-slate-100">
      {children}
    </pre>
    <TombolSalin teks={children} />
  </div>
);

const Tabel = ({ kepala, baris }) => (
  <div className="my-3 overflow-x-auto rounded-lg border border-[var(--border-subtle)]">
    <table className="w-full border-collapse text-sm">
      <thead>
        <tr className="bg-[var(--surface-sunken)]">
          {kepala.map((h) => (
            <th
              key={h}
              className="px-3 py-2 text-left text-xs font-bold tracking-wide text-[var(--text-muted)] uppercase"
            >
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {baris.map((b, i) => (
          <tr key={i} className="border-t border-[var(--border-subtle)]">
            {b.map((sel, j) => (
              <td key={j} className="px-3 py-2 align-top text-[var(--text-secondary)]">
                {j === 0 ? (
                  <code className="font-mono text-xs text-[var(--text-primary)]">{sel}</code>
                ) : (
                  sel
                )}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const Catatan = ({ nada = 'info', children }) => (
  <div
    className={cn(
      'my-4 rounded-lg border-l-4 px-4 py-3 text-sm',
      nada === 'peringatan'
        ? 'border-warning-500 bg-warning-50 text-[var(--text-secondary)] dark:bg-warning-500/10'
        : 'border-accent-600 bg-accent-50 text-[var(--text-secondary)] dark:bg-accent-500/10',
    )}
  >
    {children}
  </div>
);

/** Satu blok isi, dirender sesuai tipenya. */
const Blok = ({ blok }) => {
  if (blok.t === 'p') return <p className="my-3 leading-relaxed text-[var(--text-secondary)]">{blok.isi}</p>;
  if (blok.t === 'h') return <h3 className="mt-8 mb-2 text-base font-bold text-[var(--text-primary)]">{blok.isi}</h3>;
  if (blok.t === 'kode') return <Kode>{blok.isi}</Kode>;
  if (blok.t === 'tabel') return <Tabel kepala={blok.kepala} baris={blok.baris} />;
  if (blok.t === 'catatan') return <Catatan nada={blok.nada}>{blok.isi}</Catatan>;
  if (blok.t === 'daftar') {
    return (
      <ul className="my-3 space-y-1.5 pl-5">
        {blok.isi.map((x, i) => (
          <li key={i} className="list-disc leading-relaxed text-[var(--text-secondary)]">
            {x}
          </li>
        ))}
      </ul>
    );
  }
  return null;
};

export const DokumentasiPage = () => {
  const [aktif, setAktif] = useState(BAGIAN[0].id);
  const [navTerbuka, setNavTerbuka] = useState(false);

  const idBagian = useMemo(() => BAGIAN.map((b) => b.id), []);

  // Menyorot bagian yang sedang dibaca. Dokumentasi panjang tanpa penanda
  // posisi membuat pembaca kehilangan jejak setelah menggulung beberapa layar.
  useEffect(() => {
    const pengamat = new IntersectionObserver(
      (entri) => {
        const terlihat = entri
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        if (terlihat) setAktif(terlihat.target.id);
      },
      { rootMargin: '-80px 0px -70% 0px' },
    );

    idBagian.forEach((id) => {
      const el = document.getElementById(id);
      if (el) pengamat.observe(el);
    });
    return () => pengamat.disconnect();
  }, [idBagian]);

  return (
    <div className="min-h-screen bg-[var(--surface-canvas)]">
      <header className="sticky top-0 z-30 border-b border-[var(--border-subtle)] bg-[var(--surface-raised)]/95 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-3">
          <Link to="/" className="flex items-center gap-2">
            <Logo className="h-7 w-auto" />
            <span className="text-sm font-semibold text-[var(--text-muted)]">Dokumentasi</span>
          </Link>

          <div className="flex items-center gap-2">
            <Link to="/masuk">
              <Button variant="secondary" size="sm">
                Masuk
              </Button>
            </Link>
            <button
              type="button"
              onClick={() => setNavTerbuka((v) => !v)}
              className="rounded-lg border border-[var(--border-default)] p-2 lg:hidden"
              aria-label="Daftar isi"
            >
              {navTerbuka ? <X className="size-4" /> : <Menu className="size-4" />}
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto flex max-w-6xl gap-8 px-4 py-8">
        <nav
          className={cn(
            'shrink-0 lg:block lg:w-56',
            navTerbuka
              ? 'fixed inset-x-0 top-14 z-20 border-b border-[var(--border-subtle)] bg-[var(--surface-raised)] p-4 shadow-lg'
              : 'hidden',
          )}
          aria-label="Daftar isi"
        >
          <p className="mb-2 text-xs font-bold tracking-wide text-[var(--text-muted)] uppercase">
            Daftar isi
          </p>
          <ul className="space-y-0.5 lg:sticky lg:top-20">
            {BAGIAN.map((b) => (
              <li key={b.id}>
                <a
                  href={`#${b.id}`}
                  onClick={() => setNavTerbuka(false)}
                  className={cn(
                    'block rounded-md px-2.5 py-1.5 text-sm transition-colors',
                    aktif === b.id
                      ? 'bg-[var(--surface-sunken)] font-semibold text-[var(--text-primary)]'
                      : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)]',
                  )}
                >
                  {b.judul}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        <main className="min-w-0 flex-1">
          <div className="mb-8 flex items-center gap-3">
            <BookOpen className="size-6 text-accent-600" aria-hidden="true" />
            <div>
              <h1 className="text-2xl font-bold text-[var(--text-primary)]">Dokumentasi Crawel</h1>
              <p className="text-sm text-[var(--text-muted)]">
                Cara memakai Crawel, dari halaman pertama sampai memanggilnya dari program lain.
              </p>
            </div>
          </div>

          {BAGIAN.map((b) => (
            <section key={b.id} id={b.id} className="mb-14 scroll-mt-20">
              <h2 className="mb-1 border-b border-[var(--border-subtle)] pb-2 text-xl font-bold text-[var(--text-primary)]">
                {b.judul}
              </h2>
              {b.ringkas && <p className="mt-2 text-sm text-[var(--text-muted)]">{b.ringkas}</p>}
              {b.isi.map((blok, i) => (
                <Blok key={i} blok={blok} />
              ))}
            </section>
          ))}

          <footer className="border-t border-[var(--border-subtle)] pt-6 text-sm text-[var(--text-muted)]">
            Ada yang tidak cocok dengan kenyataan di dokumen ini? Laporkan lewat menu Lapor Bug di
            dalam dashboard. Dokumentasi yang meleset lebih berbahaya daripada yang tidak ada,
            karena ia dipercaya.
          </footer>
        </main>
      </div>
    </div>
  );
};
